/**
 * @fileoverview Text-to-Speech Utility - Web Speech API wrapper
 * 
 * @description
 * Provides text-to-speech functionality using the native Web Speech API.
 * Features zero latency, offline support, and automatic text chunking
 * for long content.
 * 
 * ## Features
 * - **Zero latency**: Native browser implementation
 * - **Offline capable**: No external API calls
 * - **Auto-chunking**: Splits long text for reliable playback
 * - **Progress tracking**: Callbacks for chunk completion
 * - **Voice selection**: Choose from available system voices
 * 
 * ## Chunking Strategy
 * ```
 * Long Text → Split by sentences → Chunk by MAX_SIZE (200 chars)
 *                                        ↓
 *                    Single sentence too long?
 *                            ↓
 *                    Split by commas/phrases
 *                            ↓
 *                    Still too long? Split by words
 * ```
 * 
 * ## Browser Limits
 * Different browsers have varying limits on utterance length:
 * - Chrome: ~500 characters
 * - Safari: ~250 characters
 * - Firefox: ~300 characters
 * We use 200 chars as a safe cross-browser limit.
 * 
 * @example
 * ```ts
 * import { tts } from '@/lib/textToSpeech';
 * 
 * // Basic usage
 * tts.speak('Hello, this is a test message.');
 * 
 * // With options
 * tts.speak('Important announcement', {
 *   rate: 1.2,
 *   pitch: 1.0,
 *   volume: 0.8
 * });
 * 
 * // With callbacks
 * tts.onEnd(() => console.log('Done speaking'));
 * tts.onProgress((current, total) => {
 *   console.log(`Chunk ${current}/${total}`);
 * });
 * 
 * // Controls
 * tts.pause();
 * tts.resume();
 * tts.stop();
 * ```
 * 
 * @module lib/textToSpeech
 * @see EmailDetailView - Uses TTS for email reading
 */

/**
 * Configuration options for text-to-speech
 * 
 * @interface TTSOptions
 * @property {number} [rate=1] - Speech rate (0.5 to 2)
 * @property {number} [pitch=1] - Voice pitch (0 to 2)
 * @property {number} [volume=1] - Volume level (0 to 1)
 * @property {number} [voiceIndex] - Index of voice from getVoices()
 */
export interface TTSOptions {
  rate?: number;      // 0.5 - 2 (default: 1)
  pitch?: number;     // 0 - 2 (default: 1)
  volume?: number;    // 0 - 1 (default: 1)
  voiceIndex?: number;
}

class TextToSpeechManager {
  private utterances: SpeechSynthesisUtterance[] = [];
  private currentIndex = 0;
  private isPlaying = false;
  private isPaused = false;
  private onEndCallback: (() => void) | null = null;
  private onProgressCallback: ((current: number, total: number) => void) | null = null;
  private currentOptions: TTSOptions = {};
  private chunks: string[] = [];

  /**
   * Check if TTS is supported
   */
  isSupported(): boolean {
    return 'speechSynthesis' in window;
  }

  /**
   * Get available voices
   */
  getVoices(): SpeechSynthesisVoice[] {
    if (!this.isSupported()) return [];
    return speechSynthesis.getVoices();
  }

  /**
   * Split text into chunks for reliable playback
   * Browsers have limits on utterance length (~250-500 chars varies by browser)
   */
  private splitIntoChunks(text: string): string[] {
    const MAX_CHUNK_SIZE = 200; // Conservative limit for cross-browser support
    const chunks: string[] = [];
    
    // Split by sentences first
    const sentences = text.match(/[^.!?]+[.!?]+[\s]*/g) || [text];
    
    let currentChunk = '';
    
    for (const sentence of sentences) {
      // If adding this sentence would exceed limit, save current chunk
      if (currentChunk.length + sentence.length > MAX_CHUNK_SIZE && currentChunk.length > 0) {
        chunks.push(currentChunk.trim());
        currentChunk = '';
      }
      
      // If single sentence is too long, split by phrases
      if (sentence.length > MAX_CHUNK_SIZE) {
        // Save any current chunk first
        if (currentChunk.length > 0) {
          chunks.push(currentChunk.trim());
          currentChunk = '';
        }
        
        // Split long sentence by commas or spaces
        const phrases = sentence.match(/[^,;]+[,;]?[\s]*/g) || [sentence];
        for (const phrase of phrases) {
          if (phrase.length > MAX_CHUNK_SIZE) {
            // Last resort: split by words
            const words = phrase.split(' ');
            for (const word of words) {
              if (currentChunk.length + word.length + 1 > MAX_CHUNK_SIZE && currentChunk.length > 0) {
                chunks.push(currentChunk.trim());
                currentChunk = '';
              }
              currentChunk += (currentChunk ? ' ' : '') + word;
            }
          } else {
            if (currentChunk.length + phrase.length > MAX_CHUNK_SIZE && currentChunk.length > 0) {
              chunks.push(currentChunk.trim());
              currentChunk = '';
            }
            currentChunk += phrase;
          }
        }
      } else {
        currentChunk += sentence;
      }
    }
    
    // Don't forget the last chunk
    if (currentChunk.trim().length > 0) {
      chunks.push(currentChunk.trim());
    }
    
    return chunks;
  }

  /**
   * Speak text aloud with automatic chunking for long content
   */
  speak(text: string, options: TTSOptions = {}): boolean {
    if (!this.isSupported() || !text.trim()) return false;

    // Stop any current speech
    this.stop();

    this.currentOptions = options;
    this.chunks = this.splitIntoChunks(text);
    this.currentIndex = 0;
    this.utterances = [];

    console.log(`[TTS] Speaking ${this.chunks.length} chunks`);

    // Create all utterances
    for (const chunk of this.chunks) {
      const utterance = new SpeechSynthesisUtterance(chunk);
      utterance.rate = options.rate ?? 1;
      utterance.pitch = options.pitch ?? 1;
      utterance.volume = options.volume ?? 1;

      const voices = this.getVoices();
      if (options.voiceIndex !== undefined && voices[options.voiceIndex]) {
        utterance.voice = voices[options.voiceIndex];
      }

      this.utterances.push(utterance);
    }

    // Start speaking
    this.isPlaying = true;
    this.isPaused = false;
    this.speakNextChunk();
    
    return true;
  }

  private speakNextChunk(): void {
    if (this.currentIndex >= this.utterances.length) {
      // All chunks done
      this.isPlaying = false;
      this.onEndCallback?.();
      return;
    }

    const utterance = this.utterances[this.currentIndex];

    utterance.onend = () => {
      this.currentIndex++;
      this.onProgressCallback?.(this.currentIndex, this.chunks.length);
      
      // Speak next chunk
      if (this.isPlaying && !this.isPaused) {
        this.speakNextChunk();
      }
    };

    utterance.onerror = (event) => {
      console.error('[TTS] Utterance error:', event);
      // Try to continue with next chunk
      this.currentIndex++;
      if (this.isPlaying && !this.isPaused && this.currentIndex < this.utterances.length) {
        this.speakNextChunk();
      } else {
        this.isPlaying = false;
        this.onEndCallback?.();
      }
    };

    speechSynthesis.speak(utterance);
  }

  /**
   * Stop speaking
   */
  stop(): void {
    if (this.isSupported()) {
      speechSynthesis.cancel();
      this.isPlaying = false;
      this.isPaused = false;
      this.currentIndex = 0;
      this.utterances = [];
      this.chunks = [];
    }
  }

  /**
   * Pause speaking
   */
  pause(): void {
    if (this.isSupported() && this.isPlaying) {
      speechSynthesis.pause();
      this.isPaused = true;
    }
  }

  /**
   * Resume speaking
   */
  resume(): void {
    if (this.isSupported() && this.isPaused) {
      speechSynthesis.resume();
      this.isPaused = false;
    }
  }

  /**
   * Check if currently speaking
   */
  isSpeaking(): boolean {
    return this.isPlaying;
  }

  /**
   * Get progress info
   */
  getProgress(): { current: number; total: number } {
    return {
      current: this.currentIndex,
      total: this.chunks.length,
    };
  }

  /**
   * Set callback for when speech ends
   */
  onEnd(callback: () => void): void {
    this.onEndCallback = callback;
  }

  /**
   * Set callback for progress updates
   */
  onProgress(callback: (current: number, total: number) => void): void {
    this.onProgressCallback = callback;
  }
}

export const tts = new TextToSpeechManager();
